<template>
  <Teleport to="body">
    <div v-if="isOpen" class="programs-evolution-overlay" @click="close"></div>

    <aside v-if="isOpen" class="programs-evolution-drawer" @click.stop>
      <header class="programs-evolution-header">
        <div>
          <h3 class="programs-evolution-title">
            <i class="bi bi-graph-up-arrow text-primary me-2"></i>
            Evolução de Programas
          </h3>
          <p class="programs-evolution-subtitle">
            Evolução de cada programa por dia de coleta
          </p>
        </div>
        <button type="button" class="btn btn-sm btn-icon btn-light" @click="close">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="programs-evolution-toolbar">
        <div class="programs-evolution-periods">
          <button
            v-for="option in periodOptions"
            :key="option.value"
            type="button"
            class="btn btn-sm"
            :class="period === option.value ? 'btn-primary' : 'btn-light'"
            @click="changePeriod(option.value)">
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="programs-evolution-body">
        <div v-if="loading" class="programs-evolution-empty">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 mb-0">Carregando evolução...</p>
        </div>

        <div v-else-if="!programsWithPoints.length" class="programs-evolution-empty">
          <i class="bi bi-bar-chart-line fs-1 text-muted"></i>
          <p class="mt-3 mb-0">Nenhuma coleta encontrada neste período.</p>
        </div>

        <template v-else>
          <section class="programs-evolution-section">
            <h4 class="programs-evolution-section-title">Resumo atual</h4>
            <div class="programs-evolution-summary-grid">
              <div
                v-for="program in programsWithPoints"
                :key="'sum-' + program.id_patient_program"
                class="programs-evolution-summary-card">
                <span class="programs-evolution-summary-label">{{ program.program_name }}</span>
                <span class="programs-evolution-summary-value">
                  {{ formatMetricValue(program.latest_value, program.metric) }}
                </span>
                <span class="programs-evolution-summary-meta">
                  {{ program.metric_label }} · {{ measurementShort(program.measurement_type) }}
                </span>
              </div>
            </div>
          </section>

          <section
            v-for="program in programsWithPoints"
            :key="'chart-' + program.id_patient_program"
            class="programs-evolution-section">
            <h4 class="programs-evolution-section-title">
              {{ program.program_name }}
              <span v-if="!program.active" class="badge badge-light ms-2">Arquivado</span>
              <span class="badge badge-light ms-2">{{ measurementShort(program.measurement_type) }}</span>
            </h4>
            <div class="programs-evolution-chart-card">
              <apexchart
                v-if="chartOptions(program)"
                type="line"
                height="220"
                :options="chartOptions(program)"
                :series="chartSeries(program)"/>
            </div>
          </section>
        </template>
      </div>
    </aside>
  </Teleport>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts/src/vue3-apexcharts';
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';
import {
  formatMetricValue,
  formatStatValue,
  measurementShort,
  metricAxisLabel,
} from '../../../helpers/programMeasurement';

const PERIOD_OPTIONS = [
  { value: 3, label: '3 meses' },
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 ano' },
  { value: null, label: 'Tudo' },
];

const METRIC_COLORS = {
  percent: '#7239ea',
  count: '#009ef7',
  seconds: '#50cd89',
  mean_seconds: '#f1bc00',
};

export default {
  name: 'ProgramsEvolutionDrawer',
  components: { apexchart: VueApexCharts },
  props: {
    isOpen: { type: Boolean, default: false },
    professionalId: { type: [Number, String], required: true },
    patientId: { type: [Number, String], required: true },
    placeId: { type: [Number, String], default: null },
  },
  emits: ['close'],
  data() {
    return {
      period: 6,
      periodOptions: PERIOD_OPTIONS,
      loading: false,
      programs: [],
    };
  },
  computed: {
    programsWithPoints() {
      return (this.programs || []).filter((program) => (program.points || []).length > 0);
    },
  },
  watch: {
    isOpen(open) {
      if (open) this.load();
    },
  },
  methods: {
    measurementShort,
    formatMetricValue,
    close() {
      this.$emit('close');
    },
    changePeriod(value) {
      this.period = value;
      this.load();
    },
    load() {
      if (!this.professionalId || !this.patientId) return;
      this.loading = true;
      const params = {};
      if (this.placeId) params.id_place = this.placeId;
      if (this.period) params.months = this.period;

      http.get(`/professionals/${this.professionalId}/patients/${this.patientId}/programs/evolution`, { params })
        .then((response) => {
          this.programs = response.data?.programs || [];
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.loading = false; });
    },
    chartSeries(program) {
      return [{
        name: program.metric_label || 'Valor',
        data: (program.points || []).map((point) => point.value ?? 0),
      }];
    },
    chartOptions(program) {
      const metric = program.metric || 'percent';
      const isPercent = metric === 'percent';
      const categories = (program.points || []).map((point) => {
        const [, month, day] = String(point.date).split('-');
        return `${day}/${month}`;
      });

      return {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: [METRIC_COLORS[metric] || METRIC_COLORS.percent],
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4, strokeWidth: 2, hover: { size: 6 } },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: { borderColor: '#eff2f5', strokeDashArray: 4 },
        xaxis: {
          categories,
          labels: { style: { fontSize: '11px', colors: '#7e8299' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: isPercent ? 100 : undefined,
          title: { text: metricAxisLabel(metric), style: { fontSize: '10px', color: '#a1a5b7', fontWeight: 500 } },
          labels: {
            style: { fontSize: '11px', colors: '#7e8299' },
            formatter: (value) => formatMetricValue(
              isPercent ? Math.round(value) : Math.round(value * 10) / 10,
              metric,
            ),
          },
        },
        tooltip: {
          y: {
            formatter: (value, { dataPointIndex }) => {
              const point = program.points?.[dataPointIndex];
              if (!point) return formatMetricValue(value, metric);

              const details = (point.stats || [])
                .map((stat) => `${stat.label}: ${formatStatValue(stat)}`)
                .join(' · ');

              return details
                ? `${formatMetricValue(point.value, metric)} (${details})`
                : formatMetricValue(point.value, metric);
            },
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.programs-evolution-overlay {
  position: fixed;
  inset: 0;
  background: rgba(24, 28, 50, 0.35);
  z-index: 1055;
}
.programs-evolution-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(520px, 100vw);
  height: 100vh;
  background: #fff;
  z-index: 1056;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.12);
}
.programs-evolution-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
  border-bottom: 1px solid #eff2f5;
}
.programs-evolution-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
}
.programs-evolution-subtitle {
  margin: 4px 0 0;
  color: #7e8299;
  font-size: 0.85rem;
}
.programs-evolution-toolbar {
  padding: 12px 20px;
  border-bottom: 1px solid #eff2f5;
}
.programs-evolution-periods {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.programs-evolution-body {
  padding: 16px 20px 28px;
  overflow: auto;
  flex: 1;
}
.programs-evolution-empty {
  text-align: center;
  color: #7e8299;
  padding: 48px 12px;
}
.programs-evolution-section {
  margin-bottom: 24px;
}
.programs-evolution-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 12px;
}
.programs-evolution-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.programs-evolution-summary-card {
  border: 1px solid #eff2f5;
  border-radius: 12px;
  padding: 12px;
  background: #f9f9fb;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.programs-evolution-summary-label {
  font-size: 0.75rem;
  color: #7e8299;
}
.programs-evolution-summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #181c32;
}
.programs-evolution-summary-meta {
  font-size: 0.7rem;
  color: #a1a5b7;
}
.programs-evolution-chart-card {
  border: 1px solid #eff2f5;
  border-radius: 12px;
  padding: 8px 8px 0;
  background: #fff;
}
</style>
